import React, { useState } from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { usePipelineThroughput } from '../../hooks/useAutocoderThroughput'
import { usePipelineQueue } from '../../hooks/usePipelineQueue'
import ThroughputPanels, {
  ThroughputCaption,
  ThroughputWindowPicker,
} from '../throughput/ThroughputPanels'
import LaneQueueView from '../throughput/LaneQueueView'
import type { ThroughputWindowKey } from '../../types/autocoderThroughput'

/**
 * PipelineThroughputTab — the admin AutoCoder Throughput page, for one
 * pipeline, inside the pipeline's own modal.
 *
 * The panels are literally the admin ones (components/throughput/
 * ThroughputPanels); only the scope differs, and that is decided server-side by
 * the endpoint this tab calls. So "what is my pipeline actually doing right
 * now" is the same question with the same answer wherever it is asked.
 *
 * "Most active pipelines" is suppressed here: inside one pipeline it is a
 * one-row ranking of itself.
 */

interface PipelineThroughputTabProps {
  customer: string
  job: string
  pipelineId: string
  /** False while the tab is not the visible one — a hidden tab must not poll. */
  active: boolean
}

const PipelineThroughputTab: React.FC<PipelineThroughputTabProps> = ({
  customer,
  job,
  pipelineId,
  active,
}) => {
  const [windowKey, setWindowKey] = useState<ThroughputWindowKey>('60m')
  const { data, loading, error, isFetching, refetch } = usePipelineThroughput(
    customer,
    job,
    pipelineId,
    windowKey,
    active,
  )

  // The queue rides the SAME cadence the server chose for the panels below it
  // (cut to the fast cadence whenever work is in flight), so the hero and the
  // numbers under it can never disagree about what is happening right now.
  const queue = usePipelineQueue(
    customer,
    job,
    pipelineId,
    data?.window.pollSeconds ?? 30,
    active,
  )

  return (
    <Box sx={{ p: 3, overflow: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>{data && <ThroughputCaption data={data} />}</Box>
        <ThroughputWindowPicker value={windowKey} onChange={setWindowKey} />
        <Tooltip title="Refresh now">
          <span>
            <IconButton aria-label="Refresh now" color="info" onClick={refetch} disabled={isFetching}>
              {isFetching ? <CircularProgress size={20} color="info" /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* The hero: the queue itself, batch by batch. Everything below is the
          measurement of what this picture is doing. */}
      <Box sx={{ mb: 3 }}>
        {queue.error ? (
          <Alert severity="warning">Coding queue unavailable — {queue.error}</Alert>
        ) : (
          <LaneQueueView
            lanes={queue.data?.lanes ?? []}
            totals={queue.data?.totals}
            batchLimit={queue.data?.batchLimit}
            loading={queue.loading}
          />
        )}
      </Box>

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="info" />
        </Box>
      )}

      {/* The queue-state snapshot panel is off too: the LaneQueueView hero
          above IS this pipeline's queue, batch by batch — rendering it again
          grouped by raw model_version was the same queue in a second
          vocabulary. */}
      {data && <ThroughputPanels data={data} showTopPipelines={false} showQueueSnapshot={false} />}
    </Box>
  )
}

export default PipelineThroughputTab
